import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import CustomPdfExportModal from 'src/components/custom-pdf-export-modal/CustomPdfExportModal';

import type { FilterModal } from '@/components/customisable-ribbon/customisableRibbonModalSchema';
import { useExportOptions } from '@/hooks/useExportOptions';

import type {
  SchedulerRegisterTableFields,
  useGetCollectionTableProps,
} from './config';
import { buildSchedulerRegisterToolbarConfig } from './registerToolbarConfig';

interface Props {
  title: string;
  /** Already-formatted count display string (e.g. `getCounter(...)` → `(7)`). */
  counter?: string;
  tableProps: ReturnType<typeof useGetCollectionTableProps>;
  getActiveRibbonFilters: () => FilterModal[];
  /**
   * Opens the create-schedule modal (owned by the page, shared with flag-off).
   * Omitted when the user manages no schedulable object type — the toolbar then
   * renders no primary action rather than one that opens an unusable form.
   */
  onCreate?: () => void;
}

/**
 * Owns `useExportOptions` rather than the page so the options are computed once,
 * by whichever of the toolbar and the legacy `ExportButton` is live.
 *
 * `CustomPdfExportModal` is mounted because `useExportOptions` emits the
 * "Custom PDF" option whenever the `pdf_export` flag is on and only no-ops its
 * click when no opener is passed — omitting the modal would ship an enabled menu
 * item that does nothing.
 */
export const SchedulerRegisterToolbar: FC<Props> = ({
  title,
  counter,
  tableProps,
  getActiveRibbonFilters,
  onCreate,
}) => {
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], { keyPrefix: 'scheduler' });
  const [showPdfModal, setShowPdfModal] = useState(false);
  // Ribbon "Edit Filters" opener, bridged up from CustomisableRibbon in relocate
  // mode. Null until `scheduler_schedule` has a perm_v2 custom-ribbon resource,
  // since the ribbon only publishes it when the edit button would show.
  const ribbonConfig = useObjectToolbarFormStore((s) => s.ribbonConfigState);
  const onShowCustomPdfModal = useCallback(() => setShowPdfModal(true), []);

  const { exportOptions, exportToPdf } =
    useExportOptions<SchedulerRegisterTableFields>({
      tableProps,
      entityLabel: title,
      getActiveRibbonFilters,
      onShowCustomPdfModal,
    });

  const toolbarConfig = useMemo(
    () =>
      buildSchedulerRegisterToolbarConfig({
        title,
        counter,
        exportOptions,
        create: onCreate
          ? { label: st('create_new_button'), onClick: onCreate }
          : undefined,
        editRibbon: ribbonConfig
          ? {
              groupLabel: t('edit'),
              label: t('objectToolbar.filterSettings'),
              onClick: ribbonConfig.openEditRibbon,
            }
          : undefined,
      }),
    [title, counter, exportOptions, onCreate, ribbonConfig, st, t]
  );

  return (
    <>
      <ObjectToolbar config={toolbarConfig} />
      <CustomPdfExportModal
        exportToPdf={async (options) => {
          await exportToPdf({
            titleOverride: options.title,
            subtitleOverride: options.subtitle,
            hideRibbon: options.hideRibbon,
            orientation: options.orientation,
          });
        }}
        entityLabel={title}
        showModal={showPdfModal}
        setShowModal={setShowPdfModal}
        showRibbonOptions={true}
      />
    </>
  );
};
